import * as THREE from 'three';
import { model, type ModelKey } from './Assets';
import { createFlatMaterial, createInkMaterial } from '../render/InkMaterial';
import { PLANET_RADIUS, type Planet } from './Planet';
import type { CharacterKind, CharacterOptions } from './Character';
import type { ItemId } from '../game/Economy';
import type { AmbienceKind } from '../game/Audio';

/** Market square centre: the player arrives here. */
export const MARKET = new THREE.Vector3(0, 1, 0.12).normalize();
export const PLAZA_RADIUS = 13;

export type ZoneDef = { name: string; dir: THREE.Vector3; radius: number; ambience: AmbienceKind };
export type Obstacle = { x: number; z: number; r: number };

export type NpcSpec = {
  id: string;
  name: string;
  color: string;
  kind: CharacterKind;
  opts: CharacterOptions;
  at: [number, number];
  /** Anywhere on the planet (overrides `at`, which is plaza-local). */
  dir?: THREE.Vector3;
  /** Local point to face (defaults to the plaza centre). */
  face?: [number, number];
  shop?: string;
  /** Lines when there's no quest business. */
  chatter: string[];
  /** Shouted now and then as a speech bubble. */
  shouts?: string[];
};

export type PickupSpec = { key: string; item: ItemId | 'coins'; at: [number, number]; dir?: THREE.Vector3; quest?: string };

/** Local plaza coordinates → world point on the ground. */
export function local(planet: Planet, x: number, z: number, lift = 0): THREE.Vector3 {
  return planet.surfacePoint(planet.offset(MARKET, x, z), lift);
}
const polar = (r: number, deg: number): [number, number] => [Math.cos((deg * Math.PI) / 180) * r, Math.sin((deg * Math.PI) / 180) * r];

/** Orient `obj` upright on the planet at its position, facing a world point. */
export function faceToward(obj: THREE.Object3D, target: THREE.Vector3, flip = 0): void {
  const up = obj.position.clone().normalize();
  const f = target.clone().sub(obj.position).projectOnPlane(up);
  if (f.lengthSq() < 1e-6) f.set(1, 0, 0).projectOnPlane(up);
  f.normalize();
  const x = new THREE.Vector3().crossVectors(up, f).normalize();
  obj.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, up, f));
  if (flip) obj.rotateY(flip);
}

// Which way each building's door faces in its own model space (radians about Y, measured from +Z).
const FRONT: Partial<Record<ModelKey, number>> = {};

export const STALLS: { shop: string; key: ModelKey; at: [number, number] }[] = [
  { shop: 'shields', key: 'marketBlue', at: polar(8.6, 20) },
  { shop: 'fletcher', key: 'marketGreen', at: polar(8.6, 92) },
  { shop: 'alchemist', key: 'marketYellow', at: polar(8.6, 164) },
  { shop: 'bakery', key: 'marketRed', at: polar(8.6, 236) },
];

const FORGE_AT = polar(10.5, 305);
const TAVERN_AT = polar(17.5, 200);
const RANGE_AT: [number, number] = [-19, -14];
/** Burnwood Gate sits in the city wall (see City.ts WALL_THETA = 0.8). */
export const GATE_Z = Math.round(PLANET_RADIUS * Math.tan(0.8) * 10) / 10;
const GATE_AT: [number, number] = [0, GATE_Z];
const MILL_AT: [number, number] = [21, -12];

export const NPCS: NpcSpec[] = [
  // --- vendors ---
  { id: 'smith', name: 'Hilda Ironhand', color: '#8a3324', kind: 'Barbarian', opts: { hideHeadgear: true, keep: ['1H_Axe'] }, at: polar(7.4, 305), shop: 'forge',
    chatter: ['Steel’s steel. Mine’s just better.'], shouts: ['Blades! Fresh off the anvil!', 'Axes for the brave, daggers for the sneaky!', 'Hammered today, swung tomorrow!'] },
  { id: 'corwin', name: 'Corwin', color: '#3b5b9a', kind: 'Knight', opts: { hideHeadgear: true, keep: ['Round_Shield'] }, at: polar(5.8, 20), shop: 'shields',
    chatter: ['A shield is just a friend that takes hits for you.'], shouts: ['Shields! Round, square, spiky!', 'Don’t meet the hills without a shield!'] },
  { id: 'pip', name: 'Pip', color: '#4f7a3a', kind: 'Rogue', opts: { keep: ['1H_Crossbow'] }, at: polar(5.8, 92), shop: 'fletcher',
    chatter: ['Hit what you aim at. Aim at things that deserve it.'], shouts: ['Crossbows! Point and click!', 'Bolts, twenty a quiver!'] },
  { id: 'ysolde', name: 'Madame Ysolde', color: '#6b4bb0', kind: 'Mage', opts: { keep: ['2H_Staff'] }, at: polar(5.8, 164), shop: 'alchemist',
    chatter: ['Everything here is perfectly safe. Mostly.'], shouts: ['Smoke bombs! Wands! Mysteries!', 'Magic for the modest budget!'] },
  { id: 'tobin', name: 'Old Tobin', color: '#a5672a', kind: 'Barbarian', opts: { hideHeadgear: true, keep: ['Mug'] }, at: polar(5.8, 236), shop: 'bakery',
    chatter: ['You can’t fight on an empty stomach. Well, you can. Badly.'], shouts: ['Sweet buns! Two coins!', 'Cider! Cold-ish!'] },
  // --- quest givers ---
  { id: 'herald', name: 'Herald Bram', color: '#c8452f', kind: 'Knight', opts: { hideHeadgear: true, keep: ['1H_Sword'] }, at: [0, -3.4],
    chatter: ['Gear up, friend. Burnwood needs every one of us.'], shouts: ['Burnwood calls! Arm yourselves!', 'To the Gate by sundown!', 'Something stirs beyond the hills!'] },
  { id: 'marigold', name: 'Lady Marigold', color: '#b0447a', kind: 'Mage', opts: { hideHeadgear: false }, at: polar(4.2, 200),
    chatter: ['One cannot be too careful with one’s purse.'] },
  { id: 'fennick', name: 'Old Fennick', color: '#7a6a55', kind: 'Mage', opts: { hideHeadgear: true }, at: polar(10.8, 60),
    chatter: ['In my day, thieves had manners.'] },
  { id: 'bard', name: 'Lute the Bard', color: '#7b52b8', kind: 'Rogue', opts: { hideHeadgear: true }, at: polar(10.5, 130),
    chatter: ['♪ Swords and shields and sweet buns too... ♪', 'Every hero needs a song. Most don’t deserve one.'] },
  { id: 'barkeep', name: 'Moll the Barkeep', color: '#a5672a', kind: 'Barbarian', opts: { hideHeadgear: true, keep: ['Mug'] }, at: [TAVERN_AT[0] + 3.4, TAVERN_AT[1] + 1.6], face: [0, 0],
    chatter: ['First drink’s on you. Second one too.'] },
  { id: 'trainer', name: 'Sergeant Vex', color: '#4f7a3a', kind: 'Barbarian', opts: { hideHeadgear: false, keep: ['2H_Axe'] }, at: [RANGE_AT[0] + 4.5, RANGE_AT[1] + 2.5], face: [0, 0],
    chatter: ['Elbows up. Chin down. Try not to cut yourself.'] },
  { id: 'captain', name: 'Captain Rook', color: '#3b5b9a', kind: 'Knight', opts: { keep: ['1H_Sword', 'Badge_Shield'] }, at: [3.0, GATE_AT[1] - 6.2], face: [0, 0],
    chatter: ['Eyes on the hills. Always.'] },
  { id: 'guard', name: 'Guard Dunley', color: '#5a6b7c', kind: 'Knight', opts: { keep: ['Rectangle_Shield'] }, at: [-3.0, GATE_AT[1] - 6.0], face: [0, 0],
    chatter: ['Move along. Unless you have food.'] },
];

export const PICKUPS: PickupSpec[] = [
  // Loose coins all over town: anyone can grab these.
  { key: 'coin1', item: 'coins', at: polar(3.2, 70) }, { key: 'coin2', item: 'coins', at: polar(11.8, 10) },
  { key: 'coin3', item: 'coins', at: polar(12, 175) }, { key: 'coin4', item: 'coins', at: polar(11.2, 280) },
  { key: 'coin5', item: 'coins', at: [-14, 4] }, { key: 'coin6', item: 'coins', at: [14, 8] },
  { key: 'coin7', item: 'coins', at: [3, 19] }, { key: 'coin8', item: 'coins', at: [17, -15] },
  // Quest items only appear once their quest is active.
  { key: 'purse', item: 'purse', at: polar(11.6, 250), quest: 'purse' },
  { key: 'ingots', item: 'ingots', at: [TAVERN_AT[0] - 3.8, TAVERN_AT[1] - 2.2], quest: 'ingots' },
  { key: 'sheet1', item: 'songsheet', at: [MILL_AT[0] - 3, MILL_AT[1] + 2.5], quest: 'lyrics' },
  { key: 'sheet2', item: 'songsheet', at: [RANGE_AT[0] + 1, RANGE_AT[1] + 5], quest: 'lyrics' },
  { key: 'sheet3', item: 'songsheet', at: [-4, 21], quest: 'lyrics' },
  { key: 'sheet4', item: 'songsheet', at: polar(12.4, 330), quest: 'lyrics' },
];

export type MarketBuild = {
  obstacles: Obstacle[];
  zones: ZoneDef[];
  dummies: THREE.Object3D[];
  wellPos: THREE.Vector3;
};

/** Build the bazaar, its buildings, props and colliders on the planet. */
export function buildMarket(planet: Planet): MarketBuild {
  const obstacles: Obstacle[] = [];
  const centre = local(planet, 0, 0);

  const put = (key: ModelKey, at: [number, number], opts: { height?: number; scale?: number; face?: [number, number]; yaw?: number; collide?: number; sink?: number } = {}) => {
    const obj = model(key, { height: opts.height, scale: opts.scale });
    obj.position.copy(local(planet, at[0], at[1], -(opts.sink ?? 0.05)));
    const face = opts.face ? local(planet, opts.face[0], opts.face[1]) : centre;
    faceToward(obj, face, (FRONT[key] ?? 0) + (opts.yaw ?? 0));
    planet.group.add(obj);
    const size = obj.userData.size as THREE.Vector3;
    const r = opts.collide ?? Math.max(size.x, size.z) * 0.45;
    if (r > 0) {
      planet.addCollider(new THREE.CylinderGeometry(r, r, size.y + 0.5, 10).translate(0, size.y / 2, 0), obj);
      obstacles.push({ x: at[0], z: at[1], r: r + 0.35 });
    }
    return obj;
  };

  // --- the square ---
  const well = put('well', [0, 0], { height: 2.6, collide: 1.3 });
  put('stage', [0, -5.6], { height: 0.9, sink: 0.4 });

  for (const s of STALLS) {
    const stall = put(s.key, s.at, { height: 3.6 });
    const [x, z] = s.at;
    const n = Math.hypot(x, z);
    const side: [number, number] = [-z / n, x / n];
    // Goods spill out beside each stall.
    put('crates', [x + side[0] * 3.6, z + side[1] * 3.6], { height: 1.2, yaw: 0.4 });
    put('barrel', [x - side[0] * 3.4, z - side[1] * 3.4], { height: 1.0 });
    put('torch', [x * 0.78 + side[0] * 2.4, z * 0.78 + side[1] * 2.4], { height: 1.9, collide: 0.15 });
    void stall;
  }
  // Shop-specific displays.
  const disp = (key: ModelKey, at: [number, number], h: number, yaw = 0) => put(key, at, { height: h, yaw, collide: 0.5 });
  disp('swordShield', polar(6.2, 4), 1.4, Math.PI);
  disp('swordShieldGold', polar(6.2, 36), 1.4, Math.PI);
  disp('tableMed', polar(6.4, 112), 1.0);
  // Ysolde's Oddities: a cluttered potion bench, a chest of curiosities, kegs of who-knows-what.
  const dress = (tableKey: ModelKey, at: [number, number], h: number, items: [ModelKey, number, number, number][]) => {
    const table = put(tableKey, at, { height: h, collide: 0.7 });
    const top = (table.userData.size as THREE.Vector3).y;
    for (const [key, ih, dx, dz] of items) {
      const it = model(key, { height: ih });
      it.position.set(dx, top - 0.02, dz);
      it.rotation.y = dx * 2.3 + dz;
      table.add(it);
    }
    return table;
  };
  dress('tableMed', polar(6.9, 150), 0.95, [
    ['bottleGreen', 0.42, -0.45, 0.1], ['bottleBrown', 0.36, -0.2, -0.15], ['bottleGreen', 0.3, 0.05, 0.2],
    ['smokebomb', 0.22, 0.35, -0.05], ['smokebomb', 0.2, 0.55, 0.18], ['bottleBrown', 0.44, 0.7, -0.2],
  ]);
  dress('tableSmall', polar(6.9, 178), 0.9, [['spellbook', 0.22, 0, 0], ['bottleGreen', 0.32, 0.32, 0.12], ['wand', 0.06, -0.3, 0.1]]);
  put('chestGold', polar(9.4, 146), { height: 0.75, collide: 0.5 });
  put('keg', polar(9.6, 182), { height: 0.9, collide: 0.45 });
  put('barrels', polar(11.4, 172), { height: 1.2 });
  put('boxes', polar(11.2, 156), { height: 1.1 });
  const staff = model('staff', { height: 1.7 });
  staff.position.copy(local(planet, ...polar(7.6, 186), -0.05));
  faceToward(staff, centre);
  staff.rotateZ(0.22);
  planet.group.add(staff);
  disp('crateBuns', polar(6.4, 222), 0.75);
  disp('crateCheese', polar(6.4, 250), 0.75);
  disp('cratePotatoes', polar(9.8, 262), 0.75);
  disp('crateCarrots', polar(9.8, 210), 0.75);
  disp('crateTomatoes', polar(12, 232), 0.75);

  // The forge: a proper building, anvil-side out.
  put('blacksmith', FORGE_AT, { height: 4.6 });
  disp('swordShield', polar(8.2, 292), 1.5, Math.PI);
  disp('keg', polar(8.6, 320), 1.0);
  put('boxes', polar(12.5, 318), { height: 1.4 });

  // Banners ring the square, each hung from a wooden frame, with bunting strung between frames.
  const banners: ModelKey[] = ['bannerRed', 'bannerYellow', 'bannerShield', 'bannerGreen'];
  const wood = createInkMaterial({ color: 0x7a4f33, lineBreak: 0.12 });
  const iron = createInkMaterial({ color: 0x4a4a52, lineBreak: 0.05 });
  const frameTops: THREE.Vector3[] = [];
  for (let i = 0; i < 12; i++) {
    const deg = i * 30 + 15;
    if ([285, 315].includes(deg)) continue; // keep the forge approach open
    const at = polar(13.6, deg);
    const frame = new THREE.Group();
    const banner = model(banners[i % 4], { height: 2.6 });
    const w = (banner.userData.size as THREE.Vector3).x;
    banner.position.y = 1.0;
    // Wall banners only have a front face; these are seen from both sides.
    banner.traverse((o) => { if ((o as THREE.Mesh).isMesh) ((o as THREE.Mesh).material as THREE.Material).side = THREE.DoubleSide; });
    frame.add(banner);
    const postH = 3.95;
    for (const x of [-w / 2 - 0.14, w / 2 + 0.14]) {
      frame.add(new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, postH, 7).translate(x, postH / 2, 0), wood));
      frame.add(new THREE.Mesh(new THREE.SphereGeometry(0.13, 8, 6).translate(x, postH + 0.06, 0), iron));
    }
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, w + 0.6, 7), wood);
    bar.rotation.z = Math.PI / 2;
    bar.position.y = 3.62;
    frame.add(bar);
    frame.traverse((o) => { o.castShadow = o.receiveShadow = true; });
    frame.position.copy(local(planet, at[0], at[1], -0.05));
    faceToward(frame, centre);
    planet.group.add(frame);
    planet.addCollider(new THREE.BoxGeometry(w + 0.5, 4, 0.35).translate(0, 2, 0), frame);
    obstacles.push({ x: at[0], z: at[1], r: w / 2 + 0.5 });
    frameTops.push(frame.position.clone().addScaledVector(frame.position.clone().normalize(), postH - 0.1));
  }
  // Bunting: a sagging rope with little pennants between each pair of frames.
  const rope = createInkMaterial({ color: 0x3a2a20, lineBreak: 0 });
  const flagMats = [0xc8452f, 0xf2b733, 0x3b6fb0, 0x4f9a4a, 0xfbf0d9].map((c) => createInkMaterial({ color: c, side: THREE.DoubleSide, lineBreak: 0.05, band: [0.05, 0.12] }));
  const flagGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(-0.17, 0, 0), new THREE.Vector3(0.17, 0, 0), new THREE.Vector3(0, -0.42, 0)]);
  flagGeo.computeVertexNormals();
  for (let i = 0; i < frameTops.length; i++) {
    const a = frameTops[i], b = frameTops[(i + 1) % frameTops.length];
    if (a.distanceTo(b) > 9) continue; // skip the gap at the forge
    const up = a.clone().add(b).normalize();
    const sag = a.distanceTo(b) * 0.12;
    const pts = Array.from({ length: 13 }, (_, k) => {
      const t = k / 12;
      return a.clone().lerp(b, t).addScaledVector(up, -sag * 4 * t * (1 - t));
    });
    const curve = new THREE.CatmullRomCurve3(pts);
    const line = new THREE.Mesh(new THREE.TubeGeometry(curve, 24, 0.025, 5), rope);
    line.castShadow = true;
    planet.group.add(line);
    const n = Math.floor(curve.getLength() / 0.55);
    for (let k = 1; k < n; k++) {
      const t = k / n;
      const p = curve.getPointAt(t);
      const tangent = curve.getTangentAt(t);
      const flag = new THREE.Mesh(flagGeo, flagMats[(i + k) % flagMats.length]);
      flag.position.copy(p);
      // Hang straight "down" toward the planet, flat face across the rope.
      const x = tangent.clone().projectOnPlane(up).normalize();
      const y = up.clone();
      flag.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, new THREE.Vector3().crossVectors(x, y)));
      flag.castShadow = true;
      planet.group.add(flag);
    }
  }

  // --- around town ---
  put('tavern', TAVERN_AT, { height: 5.4 });
  put('tableLong', [TAVERN_AT[0] + 4.8, TAVERN_AT[1] - 1.5], { height: 1.0, yaw: Math.PI / 2 });
  put('stool', [TAVERN_AT[0] + 5.6, TAVERN_AT[1] + 0.2], { height: 0.6, collide: 0.25 });
  put('barrels', [TAVERN_AT[0] - 2.6, TAVERN_AT[1] + 3.2], { height: 1.4 });

  const homes: [ModelKey, [number, number]][] = [
    ['homeA_red', [17, 3]], ['homeB_yellow', [15, 12]], ['homeA_blue', [-10, 17]], ['homeB_green', [-16, 9]],
    ['homeA_red', [10, -19]], ['homeB_yellow', [-3, -20]], ['homeA_blue', [-23, -3]], ['homeB_green', [21, -2]],
  ];
  for (const [k, at] of homes) put(k, at, { height: 4.6 });
  put('towerA', [-8, 23], { height: 7 });
  put('towerB', [8, 23], { height: 7 });
  put('barracks', [11, 26], { height: 4.4, face: [0, 26] });
  put('windmill', MILL_AT, { height: 7.5 });
  put('range', RANGE_AT, { height: 4 });

  // Training dummies: post + sack body, they wobble when struck.
  const dummies: THREE.Object3D[] = [];
  const sack = createInkMaterial({ color: 0xcfa86a, lineBreak: 0.1 });
  const post = createInkMaterial({ color: 0x7a4f33, lineBreak: 0.1 });
  for (let i = 0; i < 3; i++) {
    const at: [number, number] = [RANGE_AT[0] + 5 + i * 2.4, RANGE_AT[1] - 3 + i * 0.6];
    const d = new THREE.Group();
    const wob = new THREE.Group();
    wob.add(new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 1.6, 6).translate(0, 0.8, 0), post));
    wob.add(new THREE.Mesh(new THREE.CapsuleGeometry(0.28, 0.45, 4, 10).translate(0, 1.25, 0), sack));
    wob.add(new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8).translate(0, 1.85, 0), sack));
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.1, 6), post);
    arm.rotation.z = Math.PI / 2;
    arm.position.y = 1.4;
    wob.add(arm);
    d.add(wob);
    d.userData.wobble = wob;
    d.traverse((o) => { o.castShadow = o.receiveShadow = true; });
    d.position.copy(local(planet, at[0], at[1]));
    faceToward(d, local(planet, 0, 0));
    planet.group.add(d);
    planet.addCollider(new THREE.CylinderGeometry(0.3, 0.3, 2, 8).translate(0, 1, 0), d);
    dummies.push(d);
  }

  // --- The Dragon Road: a waymarker where the road leaves town for Mount Cinder ---
  const sign = new THREE.Group();
  const signWood = createInkMaterial({ color: 0x7a4f33, lineBreak: 0.1 });
  const signBoard = createInkMaterial({ color: 0xe8d3a8, lineBreak: 0.05 });
  sign.add(new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 3.2, 7).translate(0, 1.6, 0), signWood));
  for (const [y, yaw, w] of [[2.7, 0.15, 2.0], [2.15, -0.5, 1.6], [1.6, 0.9, 1.7]] as const) {
    const board = new THREE.Mesh(new THREE.BoxGeometry(w, 0.38, 0.08).translate(w / 2 - 0.15, 0, 0), signBoard);
    board.position.y = y;
    board.rotation.y = yaw;
    sign.add(board);
  }
  sign.add(new THREE.Mesh(new THREE.SphereGeometry(0.2, 10, 8).translate(0, 3.25, 0), createFlatMaterial(0xff7a2a, 0)));
  sign.traverse((o) => { o.castShadow = o.receiveShadow = true; });
  sign.position.copy(local(planet, 2.6, GATE_AT[1] - 2));
  faceToward(sign, centre);
  planet.group.add(sign);
  planet.addCollider(new THREE.CylinderGeometry(0.25, 0.25, 3.2, 6).translate(0, 1.6, 0), sign);
  for (const x of [-3.2, 3.2]) put('torch', [x, GATE_AT[1] - 0.5], { height: 2.2, collide: 0.15 });

  // Crowd keeps clear of named NPC spots.
  for (const n of NPCS) if (!n.dir) obstacles.push({ x: n.at[0], z: n.at[1], r: 0.8 });

  const zones: ZoneDef[] = [
    { name: 'Ironhand Forge', dir: planet.offset(MARKET, ...FORGE_AT), radius: 5, ambience: 'forge' },
    { name: 'The Rusty Tankard', dir: planet.offset(MARKET, ...TAVERN_AT), radius: 6, ambience: 'tavern' },
    { name: 'Training Yard', dir: planet.offset(MARKET, RANGE_AT[0] + 3, RANGE_AT[1]), radius: 7, ambience: 'wind' },
    { name: 'The Dragon Road', dir: planet.offset(MARKET, 0, GATE_AT[1] - 2), radius: 7, ambience: 'wind' },
    { name: 'Old Mill', dir: planet.offset(MARKET, ...MILL_AT), radius: 6, ambience: 'forest' },
    { name: 'Grand Bazaar', dir: MARKET.clone(), radius: PLAZA_RADIUS, ambience: 'crowd' },
  ];
  return { obstacles, zones, dummies, wellPos: well.position.clone() };
}

/** Pavement and roads for the planet constructor. */
export function marketPaving(planet: { offset: (d: THREE.Vector3, x: number, z: number) => THREE.Vector3 }) {
  const o = (x: number, z: number) => planet.offset(MARKET, x, z);
  return [
    { dir: MARKET.clone(), radius: PLAZA_RADIUS + 1.5 },
    { from: o(0, 10), to: o(0, GATE_Z + 2), width: 4.5 },
    { from: o(-8, -6), to: RANGE_AT ? o(RANGE_AT[0] + 3, RANGE_AT[1]) : o(-18, -14), width: 3 },
    { from: o(-10, 8), to: o(TAVERN_AT[0] + 2, TAVERN_AT[1]), width: 3 },
    { from: o(9, -8), to: o(MILL_AT[0] - 2, MILL_AT[1]), width: 2.6 },
  ];
}

/** Flattened pads under the town so buildings sit level. */
export function marketPads(planet: { offset: (d: THREE.Vector3, x: number, z: number) => THREE.Vector3 }) {
  const o = (x: number, z: number) => planet.offset(MARKET, x, z);
  return [
    { name: 'plaza', dir: MARKET.clone(), radius: 19 },
    { name: 'gate', dir: o(0, GATE_Z - 3), radius: 9 },
    { name: 'range', dir: o(RANGE_AT[0] + 2, RANGE_AT[1]), radius: 8 },
    { name: 'mill', dir: o(MILL_AT[0], MILL_AT[1]), radius: 6 },
    { name: 'tavern', dir: o(TAVERN_AT[0], TAVERN_AT[1]), radius: 7 },
  ];
}
